import DisplayComments from "./DisplayComments";
import TableCellAdjustment from "./TableCellAdjustment";
import TableCellSafetyMargin from "./TableCellSafetyMargin";
import TableCellWeek1 from "./TableCellWeek1";
import TableCellWeek2 from "./TableCellWeek2";
import TableCellWeek3 from "./TableCellWeek3";
import TableCellWeek4 from "./TableCellWeek4";
import TableCellWeek5 from "./TableCellWeek5";
import { formatNumber } from "./defineUtils";
import CustomerTrustPopover from "./CustomerTrustPopover";
import CustomerTypePopover from "./CustomerTypePopover";
import SKUNamePopOver from "./SKUNamePopOver";
import SKUNumberPopOver from "./SKUNumberPopOver";
import { green, orange, red, grey, blue } from "@mui/material/colors";
import { Chip } from "@mui/material";
import Diversity2OutlinedIcon from "@mui/icons-material/Diversity2Outlined";
import TableCellTender from "./TableCellTender";
import TableCellDepletionType from "./TableCellDepletionType";

/** DataGrid field used for the Time Depletion / depletion-type column across breakdown views. */
export const DEPLETION_TYPE_COLUMN_FIELD = "depletionModelID";

/**
 * Sort/filter value for the depletion-type column: display name first, then internal key.
 * Use with field {@link DEPLETION_TYPE_COLUMN_FIELD} so the grid sorts by label, not numeric ID.
 */
export const depletionTypeColumnSortProps = {
	sortable: true,
	/** Supports MUI X v6 `(value, row, …)` and legacy `(params)` where `params.row` holds the row. */
	valueGetter: (valueOrParams, rowMaybe) => {
		const row =
			rowMaybe ??
			(valueOrParams &&
			typeof valueOrParams === "object" &&
			"row" in valueOrParams
				? valueOrParams.row
				: null);
		if (!row) return "";
		const name = row.depletionModelName;
		if (name != null && String(name).trim() !== "") {
			return String(name).trim();
		}
		const key = row.depletionModel;
		return key != null && String(key).trim() !== "" ? String(key).trim() : "";
	},
	sortComparator: (v1, v2) =>
		String(v1 ?? "").localeCompare(String(v2 ?? ""), undefined, {
			sensitivity: "base",
			numeric: true,
		}),
};

/**
 * Hides “Depletion type” for Weekly scenarios only (shown for Monthly, etc.).
 * @param {Array} cols – column defs (not mutated)
 * @param {string} [scenarioPeriodType] – e.g. state.selectedScenario.periodType
 */
export function filterDepletionTypeColumnForScenario(cols, scenarioPeriodType) {
	if (!Array.isArray(cols)) return cols;
	if (scenarioPeriodType === "Weekly") {
		return cols.filter((c) => c.field !== DEPLETION_TYPE_COLUMN_FIELD);
	}
	return cols;
}

export const weeklyDepCols = [
	{
		field: "name",
		headerName: "Customer / CG ",
		hideSortIcons: "false",
		width: 300,
	},
	{
		field: "customerNumber",
		headerName: "Customer Number",
		hideSortIcons: "false",
		width: 120,
	},
	{
		field: "type",
		headerName: "Customer Type",
		disableColumnMenu: true,
		hideSortIcons: true,
		filterable: false,
		width: 170,
		renderHeader: (params) => {
			return <CustomerTypePopover />;
		},
	},
	{
		field: "skuName",
		headerName: "SKU",
		disableColumnMenu: false,
		filterable: true,
		hideSortIcons: "false",
		width: 120,
		renderHeader: (params) => {
			return <SKUNamePopOver />;
		},
	},
	{
		field: "skuNumb",
		headerName: "SKU Number",
		disableColumnMenu: false,
		filterable: true,
		hideSortIcons: "false",
		width: 120,
		renderHeader: (params) => {
			return <SKUNumberPopOver />;
		},
	},
	{
		field: "depletionModelID",
		headerName: "Depletion type",
		disableColumnMenu: false,
		filterable: false,
		width: 180,
		...depletionTypeColumnSortProps,
		renderCell: TableCellDepletionType,
	},
	// {
	// 	field: "customerTrustLevel",
	// 	headerName: "Relationship Level",
	// 	disableColumnMenu: true,
	// 	hideSortIcons: true,
	// 	filterable: false,
	// 	width: 140,
	// 	renderHeader: (params) => {
	// 		return <CustomerTrustPopover />;
	// 	},
	// 	renderCell: (params) => {
	// 		return (
	// 			<Chip
	// 				sx={{ height: "1.8rem", minHeight: "1.8rem", padding: "0.5rem" }}
	// 				icon={
	// 					<Diversity2OutlinedIcon
	// 						sx={{
	// 							fontSize: 18,
	// 							color: `${getChipProps(params).iconColor} !important`,
	// 						}}
	// 					/>
	// 				}
	// 				{...getChipProps(params)}
	// 			/>
	// 		);
	// 	},
	// },
	{
		field: "skuPercentage",
		headerName: "SKU Market Share %",
		disableColumnMenu: false,
		filterable: true,
		align: "center",
		hideSortIcons: "false",
		// valueGetter: (params) => {
		//    return `${params.row.skuPercentage}%`;
		// },
		valueParser: (value) => parseFloat(value.replace("%", "")),
		valueFormatter: ({ value }) => `${value}%`,
		sortComparator: (v1, v2, param1, param2) => parseFloat(v1) - parseFloat(v2),
	},
	{
		field: "customerSharePercentage",
		headerName: "Cust Share %",
		disableColumnMenu: false,
		filterable: true,
		align: "center",
		hideSortIcons: "false",
		// valueGetter: (params) => {
		//    return `${params.row.customerSharePercentage}%`;
		// },
		valueParser: (value) => parseFloat(value.replace("%", "")),
		valueFormatter: ({ value }) => `${value}%`,
		sortComparator: (v1, v2, param1, param2) => parseFloat(v1) - parseFloat(v2),
	},
	{
		field: "unusedQuota",
		headerName: "Unused Quota - Previous Period",
		disableColumnMenu: false,
		filterable: true,
		align: "center",
		hideSortIcons: "false",
		valueFormatter: (params) => {
			const formattedValue = params.value?.toLocaleString('en-US');
			return formattedValue;
		},
	},
	{
		field: "sit",
		headerName: "SIT",
		disableColumnMenu: false,
		filterable: true,
		align: "center",
		hideSortIcons: "false",
		valueFormatter: (params) => {
			const formattedValue = params.value?.toLocaleString('en-US');
			return formattedValue;
		},
	},
	{
		field: "initialQuota",
		headerName: "Initial Quota",
		disableColumnMenu: false,
		filterable: true,
		align: "center",
		hideSortIcons: "false",
		valueFormatter: (params) => {
			const formattedValue = params.value?.toLocaleString('en-US');
			return formattedValue;
		},
	},
	{
		field: "safetyMargin",
		headerName: "Safety margin %",
		width: 100,
		renderCell: TableCellSafetyMargin,
	},
	{
		field: "tender",
		headerName: "Tender",
		width: 100,
		renderCell: TableCellTender,
	},
	{
		field: "adjustment",
		headerName: "Adjustment",
		width: 100,
		renderCell: TableCellAdjustment,
	},
	{
		field: "quota",
		headerName: "Quota",
		valueFormatter: (params) => {
			const formattedValue = params.value?.toLocaleString('en-US');
			return formattedValue;
		},
	},
	{
		field: "weekly1",
		headerName: "Week1",
		width: 100,
		renderCell: TableCellWeek1,
	},
	{
		field: "weekly2",
		headerName: "Week2",
		width: 100,
		renderCell: TableCellWeek2,
	},
	{
		field: "weekly3",
		headerName: "Week3",
		width: 100,
		renderCell: TableCellWeek3,
	},
	{
		field: "weekly4",
		headerName: "Week4",
		width: 100,
		renderCell: TableCellWeek4,
	},
	{
		field: "weekly5",
		headerName: "Week5",
		width: 100,
		renderCell: TableCellWeek5,
	},
	{
		field: "customerQuotaComments",
		headerName: "Comments",
		renderCell: DisplayComments,
	},
];

export function getWeeklyDepColsForPeriodType(includeWeek5) {
	if (includeWeek5) return weeklyDepCols;
	return weeklyDepCols.filter((col) => col.field !== "weekly5");
}

export const biWeeklyDepCols = [
	{
		field: "name",
		headerName: "Customer / CG ",
		hideSortIcons: "false",
		width: 220,
	},
	{
		field: "customerNumber",
		headerName: "Customer Number",
		hideSortIcons: "false",
		width: 120,
	},
	{
		field: "type",
		headerName: "Customer Type",
		disableColumnMenu: true,
		hideSortIcons: true,
		filterable: false,
		width: 165,
		renderHeader: (params) => {
			return <CustomerTypePopover />;
		},
	},
	{
		field: "skuName",
		headerName: "SKU",
		disableColumnMenu: false,
		filterable: true,
		hideSortIcons: "false",
		width: 120,
		renderHeader: (params) => {
			return <SKUNamePopOver />;
		},
	},
	{
		field: "skuNumb",
		headerName: "SKU Number",
		disableColumnMenu: false,
		filterable: true,
		hideSortIcons: "false",
		width: 120,
		renderHeader: (params) => {
			return <SKUNumberPopOver />;
		},
	},
	{
		field: "depletionModelID",
		headerName: "Depletion type",
		disableColumnMenu: false,
		filterable: false,
		width: 180,
		...depletionTypeColumnSortProps,
		renderCell: TableCellDepletionType,
	},
	// {
	// 	field: "customerTrustLevel",
	// 	headerName: "Relationship Level",
	// 	disableColumnMenu: true,
	// 	hideSortIcons: true,
	// 	filterable: false,
	// 	width: 140,
	// 	renderHeader: (params) => {
	// 		return <CustomerTrustPopover />;
	// 	},
	// 	renderCell: (params) => {
	// 		const chipProps = getChipProps(params);
	// 		return (
	// 			<Chip
	// 				sx={{ height: "1.8rem", minHeight: "1.8rem", padding: "0.5rem" }}
	// 				icon={
	// 					<Diversity2OutlinedIcon
	// 						sx={{
	// 							fontSize: 18,
	// 							color: `${getChipProps(params).iconColor} !important`,
	// 						}}
	// 					/>
	// 				}
	// 				{...getChipProps(params)}
	// 			/>
	// 		);
	// 	},
	// },
	{
		field: "skuPercentage",
		headerName: "SKU Market Share %",
		disableColumnMenu: false,
		filterable: true,
		align: "center",
		hideSortIcons: "false",
		// valueGetter: (params) => {
		//    return `${params.row.skuPercentage}%`;
		// },
		valueParser: (value) => parseFloat(value.replace("%", "")),
		valueFormatter: ({ value }) => `${value}%`,
		sortComparator: (v1, v2, param1, param2) => parseFloat(v1) - parseFloat(v2),
	},
	{
		field: "customerSharePercentage",
		headerName: "Cust Share %",
		disableColumnMenu: false,
		filterable: true,
		align: "center",
		hideSortIcons: "false",
		// valueGetter: (params) => {
		//    return `${params.row.customerSharePercentage}%`;
		// },
		valueParser: (value) => parseFloat(value.replace("%", "")),
		valueFormatter: ({ value }) => `${value}%`,
		sortComparator: (v1, v2, param1, param2) => parseFloat(v1) - parseFloat(v2),
	},
	{
		field: "unusedQuota",
		headerName: "Unused Quota - Previous Period",
		disableColumnMenu: false,
		filterable: true,
		align: "center",
		hideSortIcons: "false",
		valueFormatter: (params) => {
			const formattedValue = params.value?.toLocaleString('en-US');
			return formattedValue;
		},
	},
	{
		field: "sit",
		headerName: "SIT",
		disableColumnMenu: false,
		filterable: true,
		align: "center",
		hideSortIcons: "false",
		valueFormatter: (params) => {
			const formattedValue = params.value?.toLocaleString('en-US');
			return formattedValue;
		},
	},
	{
		field: "initialQuota",
		headerName: "Initial Quota",
		disableColumnMenu: false,
		filterable: true,
		align: "center",
		hideSortIcons: "false",
		valueFormatter: (params) => {
			const formattedValue = params.value?.toLocaleString('en-US');
			return formattedValue;
		},
	},
	{
		field: "safetyMargin",
		headerName: "Safety margin %",
		width: 100,
		renderCell: TableCellSafetyMargin,
	},
	{
		field: "tender",
		headerName: "Tender",
		width: 100,
		renderCell: TableCellTender,
	},
	{
		field: "adjustment",
		headerName: "Adjustment",
		width: 100,
		renderCell: TableCellAdjustment,
	},
	{
		field: "quota",
		headerName: "Quota",
		valueFormatter: (params) => {
			const formattedValue = params.value?.toLocaleString('en-US');
			return formattedValue;
		},
	},
	{
		field: "weekly1",
		headerName: "First Half",
		width: 100,
		renderCell: TableCellWeek1,
	},
	{
		field: "weekly2",
		headerName: "Second Half",
		width: 100,
		renderCell: TableCellWeek2,
	},
	{
		field: "customerQuotaComments",
		headerName: "Comments",
		renderCell: DisplayComments,
	},
];

export const noDepCols = [
	{
		field: "name",
		headerName: "Customer / CG ",
		hideSortIcons: "false",
		width: 250,
	},
	{
		field: "customerNumber",
		headerName: "Customer Number",
		hideSortIcons: "false",
		width: 110,
	},
	{
		field: "type",
		headerName: "Customer Type",
		disableColumnMenu: true,
		hideSortIcons: true,
		filterable: false,
		align: "center",
		width: 180,
		renderHeader: (params) => {
			return <CustomerTypePopover />;
		},
	},
	{
		field: "skuName",
		headerName: "SKU",
		disableColumnMenu: false,
		filterable: true,
		width: 200,
		hideSortIcons: "false",
		renderHeader: (params) => {
			return <SKUNamePopOver />;
		},
	},
	{
		field: "skuNumb",
		headerName: "SKU Number",
		disableColumnMenu: false,
		filterable: true,
		hideSortIcons: "false",
		width: 120,
		renderHeader: (params) => {
			return <SKUNumberPopOver />;
		},
	},
	{
		field: "depletionModelID",
		headerName: "Depletion type",
		disableColumnMenu: false,
		filterable: false,
		width: 180,
		...depletionTypeColumnSortProps,
		renderCell: TableCellDepletionType,
	},
	// {
	// 	field: "customerTrustLevel",
	// 	headerName: "Relationship Level",
	// 	disableColumnMenu: true,
	// 	hideSortIcons: true,
	// 	filterable: false,
	// 	align: "center",
	// 	width: 140,
	// 	renderHeader: (params) => {
	// 		return <CustomerTrustPopover />;
	// 	},
	// 	renderCell: (params) => {
	// 		const chipProps = getChipProps(params);
	// 		return (
	// 			<Chip
	// 				sx={{ height: "1.8rem", minHeight: "1.8rem", padding: "0.5rem" }}
	// 				icon={
	// 					<Diversity2OutlinedIcon
	// 						sx={{
	// 							fontSize: 18,
	// 							color: `${getChipProps(params).iconColor} !important`,
	// 						}}
	// 					/>
	// 				}
	// 				{...getChipProps(params)}
	// 			/>
	// 		);
	// 	},
	// },
	{
		field: "skuPercentage",
		headerName: "SKU Market Share %",
		disableColumnMenu: false,
		filterable: true,
		hideSortIcons: "false",
		align: "center",
		width: 120,
		// valueGetter: (params) => {
		//    return `${params.row.skuPercentage}%`;
		// },
		valueParser: (value) => parseFloat(value.replace("%", "")),
		valueFormatter: ({ value }) => `${value}%`,
		sortComparator: (v1, v2, param1, param2) => parseFloat(v1) - parseFloat(v2),
	},
	{
		field: "customerSharePercentage",
		headerName: "Cust Share %",
		disableColumnMenu: false,
		filterable: true,
		hideSortIcons: "false",
		align: "center",
		width: 70,
		// valueGetter: (params) => {
		//    return `${params.row.customerSharePercentage}%`;
		// },
		valueParser: (value) => parseFloat(value.replace("%", "")),
		valueFormatter: ({ value }) => `${value}%`,
		sortComparator: (v1, v2, param1, param2) => parseFloat(v1) - parseFloat(v2),
	},
	{
		field: "unusedQuota",
		headerName: "Unused Quota - Previous Period",
		disableColumnMenu: false,
		filterable: true,
		hideSortIcons: "false",
		width: 70,
		align: "center",
		valueFormatter: (params) => {
			const formattedValue = params.value?.toLocaleString('en-US');
			return formattedValue;
		},
	},
	{
		field: "sit",
		headerName: "SIT",
		disableColumnMenu: false,
		filterable: true,
		hideSortIcons: "false",
		width: 70,
		align: "center",
		valueFormatter: (params) => {
			const formattedValue = params.value?.toLocaleString('en-US');
			return formattedValue;
		},
	},
	{
		field: "initialQuota",
		headerName: "Initial Quota",
		disableColumnMenu: false,
		filterable: true,
		hideSortIcons: "false",
		width: 70,
		align: "center",
		valueFormatter: (params) => {
			const formattedValue = params.value?.toLocaleString('en-US');
			return formattedValue;
		},
	},
	{
		field: "safetyMargin",
		headerName: "Safety margin %",
		width: 100,
		align: "center",
		renderCell: TableCellSafetyMargin,
	},
	{
		field: "tender",
		headerName: "Tender",
		width: 100,
		renderCell: TableCellTender,
	},
	{
		field: "adjustment",
		headerName: "Adjustment",
		width: 100,
		align: "center",
		renderCell: TableCellAdjustment,
	},
	{
		field: "quota",
		headerName: "Quota",
		width: 70,
		align: "center",
		valueFormatter: (params) => {
			const formattedValue = params.value?.toLocaleString('en-US');
			return formattedValue;
		},
	},
	{
		field: "customerQuotaComments",
		headerName: "Comments",
		align: "center",
		renderCell: DisplayComments,
	},
];

export const summaryView = [
	{
		field: "name",
		headerName: "Customer / CG ",
		flex: 2,
	},
	{
		field: "customerNumber",
		headerName: "Customer Number",
		flex: 1,
	},
	{
		field: "skuName",
		headerName: "SKU",
		disableColumnMenu: false,
		filterable: true,
		hideSortIcons: "false",
		flex: 2,
		renderHeader: (params) => {
			return <SKUNamePopOver />;
		},
	},
	{
		field: "skuNumb",
		headerName: "SKU Number",
		disableColumnMenu: false,
		filterable: true,
		hideSortIcons: "false",
		flex: 1,
		renderHeader: (params) => {
			return <SKUNumberPopOver />;
		},
	},
	{
		field: "quota",
		headerName: "Quota",
		flex: 1,
		valueFormatter: (params) => {
			const formattedValue = params.value?.toLocaleString('en-US');
			return formattedValue;
		},
	},
];

function getChipProps(params) {
	if (params.value === "Low") {
		return {
			label: "Low",
			style: {
				fontWeight: "bold",
				//borderColor: green[500],
				backgroundColor: red[50],
				color: red[500],
			},
			iconColor: red[500],
		};
	} else if (params.value === "Medium") {
		return {
			label: "Medium",
			style: {
				fontWeight: "bold",
				//borderColor: orange[500],
				backgroundColor: orange[50],
				color: orange[500],
			},
			iconColor: orange[500],
		};
	} else if (params.value === "High") {
		return {
			label: "High",
			style: {
				fontWeight: "bold",
				//borderColor: green[500],
				backgroundColor: green[50],
				color: green[500],
			},
			iconColor: green[500],
		};
	} else if (params.value === "Severe") {
		return {
			label: "Severe",
			style: {
				fontWeight: "bold",
				//borderColor: grey[500],
				backgroundColor: grey[50],
				color: grey[500],
			},
			iconColor: grey[500],
		};
	} else {
		return {
			label: "Other",
			style: {
				fontWeight: "bold",
				//borderColor: blue[500],
				backgroundColor: blue[50],
				color: blue[500],
			},
			iconColor: blue[500],
		};
	}
}
